import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Award, 
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  UserCheck,
  Crown
} from 'lucide-react';

const membershipTypes = [
  {
    type: 'founding',
    title: 'Founding Members',
    description: 'Signatories of the UIEHEC Founding Memorandum of Understanding (MoU).',
    features: [
      'Founding Member recognition',
      'Premium certificate design',
      'Voting rights in all matters',
      'Priority access to programs',
      'Board meeting participation',
      'Special event invitations',
      'Logo on website',
    ],
    featured: false,
    icon: Crown,
    note: 'By invitation only'
  },
  {
    type: 'full',
    title: 'Full Members',
    description: 'Recognized institutions and organizations joining the union after its establishment.',
    features: [
      'Official UIEHEC Digital Membership Certificate',
      'Access to global academic and professional network',
      'Participation in international events and conferences',
      'Collaboration opportunities with universities and research centers',
      'Possibility to serve on committees and working groups',
      'Institutional visibility in UIEHEC Members Directory',
      'Full voting rights',
    ],
    featured: true,
    icon: Award,
  },
  {
    type: 'associate',
    title: 'Associate Members',
    description: 'Organizations that support the mission and activities of UIEHEC.',
    features: [
      'Associate membership certificate',
      'Event access and participation',
      'Networking opportunities',
      'Resource access',
      'Newsletter subscription',
      'Directory listing',
    ],
    featured: false,
    icon: Users,
  },
  {
    type: 'honorary',
    title: 'Honorary Members',
    description: 'Individuals who have made distinguished contributions to education and international cooperation.',
    features: [
      'Honorary membership certificate',
      'Special recognition',
      'Event invitations',
      'Advisory role opportunities',
    ],
    featured: false,
    icon: UserCheck,
    note: 'By nomination'
  },
];

const benefits = [
  'Official UIEHEC Digital Membership Certificate',
  'Access to a global academic and professional network',
  'Participation in international events, conferences, and policy forums',
  'Opportunities for collaboration with universities, think tanks, and research centers',
  'Possibility to serve on committees, boards, and working groups',
  'Institutional visibility in the UIEHEC Members Directory',
];

export default function MembershipPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Award className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Membership
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Join Our Global Network
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              UIEHEC membership is not merely "using a logo"; it is a strategic partnership offering 
              long-term collaboration, joint projects, and representation at the policy level.
            </p>
          </div>
        </div>
      </section>

      {/* Membership Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#0E285B] mb-4">
              Membership Benefits
            </h2>
            <div className="w-20 h-1 bg-[#F5A623] mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-sm">
                <CheckCircle className="h-5 w-5 text-[#F5A623] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Categories */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#0E285B] mb-4">
              Membership Categories
            </h2>
            <div className="w-20 h-1 bg-[#F5A623] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {membershipTypes.map((membership, index) => {
              const Icon = membership.icon;
              return (
                <div 
                  key={index}
                  className={`bg-white p-8 rounded-sm relative ${
                    membership.featured 
                      ? 'border-2 border-[#F5A623] shadow-lg' 
                      : 'border border-slate-200 shadow-sm'
                  }`}
                  data-testid={`membership-card-${membership.type}`}
                >
                  {membership.featured && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#F5A623] text-[#0E285B] text-xs font-semibold px-4 py-1 rounded-full uppercase">
                      Recommended
                    </div>
                  )}
                  
                  <div className="h-14 w-14 bg-[#0E285B] rounded-sm flex items-center justify-center mb-6">
                    <Icon className="h-7 w-7 text-[#F5A623]" />
                  </div>
                  
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-3">
                    {membership.title}
                  </h3>
                  
                  <p className="text-slate-600 text-sm mb-6">
                    {membership.description}
                  </p>
                  
                  <ul className="space-y-2 mb-6">
                    {membership.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-[#F5A623] mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {membership.note ? (
                    <p className="text-sm text-[#F5A623] font-medium">{membership.note}</p>
                  ) : (
                    <Link to="/apply">
                      <Button 
                        className={`w-full rounded-sm ${
                          membership.featured 
                            ? 'bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90' 
                            : 'bg-[#0E285B] text-white hover:bg-[#0E285B]/90'
                        }`}
                        data-testid={`apply-${membership.type}-btn`}
                      >
                        Apply Now
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#0E285B] mb-4">
              Application Process
            </h2>
            <div className="w-20 h-1 bg-[#F5A623] mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { step: '01', title: 'Complete Form', desc: 'Fill out the online application form accurately' },
              { step: '02', title: 'Upload Documents', desc: 'Submit required institutional documents' },
              { step: '03', title: 'Preliminary Review', desc: 'Secretariat reviews for completeness' },
              { step: '04', title: 'Board Evaluation', desc: 'Executive Board evaluates alignment' },
              { step: '05', title: 'Decision', desc: 'Receive notification of decision' },
              { step: '06', title: 'Activation', desc: 'Certificate issued and directory listing' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="font-['Playfair_Display'] text-4xl font-bold text-[#F5A623]/30 mb-3">
                  {item.step}
                </div>
                <h3 className="font-semibold text-[#0E285B] mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0E285B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-6">
            Ready to Apply?
          </h2>
          <p className="text-slate-300 mb-8">
            Start your membership application today and join our global community 
            of educational excellence.
          </p>
          <Link to="/apply">
            <Button 
              size="lg"
              className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 h-12 px-8 rounded-sm font-semibold"
              data-testid="membership-cta-apply-btn"
            >
              Start Application
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
